import { useTranslation } from 'react-i18next'

import { Link } from 'react-router-dom'

import { ButtonLink, buttonClasses } from '@/design-system/Button'
import { EmberField, useMagnetic, useParallax } from '@/design-system/effects'
import { Container } from '@/design-system/primitives'
import { formatDate, googleCalendarUrl } from '@/lib/datetime'

import { Countdown } from './Countdown'
import { EventTitle } from './EventTitle'
import { VisitorBubble } from './VisitorBubble'
import type { PublicContentBlock, PublicEvent } from '../types'
import { readBooleanSetting, readI18nSetting } from '../types'

interface HeroProps {
  event: PublicEvent
  maskPolicy: PublicContentBlock | undefined
}

export function Hero({ event, maskPolicy }: HeroProps) {
  const { t, i18n } = useTranslation()
  // Le logo glisse moins vite que la page, et l'appel principal attire le
  // curseur : deux touches de profondeur, inertes au doigt et en mouvement réduit.
  const logoRef = useParallax<HTMLImageElement>(0.18)
  const ticketRef = useMagnetic<HTMLAnchorElement>(0.2)

  const countdownEnabled = readBooleanSetting(event.settings, 'countdown.enabled', true)
  const configuredTarget = event.settings['countdown.target_at']
  const target = typeof configuredTarget === 'string' && configuredTarget
    ? configuredTarget
    : event.starts_at

  const textBefore = readI18nSetting(event.settings, 'countdown.text_before', i18n.language, event.locale)
  const textAfter = readI18nSetting(event.settings, 'countdown.text_after', i18n.language, event.locale)

  const place = [event.venue_name, event.city].filter(Boolean).join(' · ')
  const tagline = event.tagline
    ? event.tagline.split('.').map((part) => part.trim()).filter(Boolean)
    : []

  return (
    <header className="grain relative isolate overflow-hidden">
      {/* Le visuel de l'édition n'est PAS un fond de hero : c'est une affiche,
          avec son propre titre. Le superposer au nôtre donnait deux fois le
          même mot, l'un sur l'autre. Il a sa section, plus bas, où il se voit
          en entier. */}

      {/* Aurore : deux halos qui dérivent derrière le titre. Décorative,
          donc masquée aux lecteurs d'écran et insensible aux clics. */}
      <div aria-hidden className="aurora -z-10" />

      {/* Des braises qui montent derrière le titre, comme d'un feu. */}
      <EmberField />

      {/* Lueur de braise : l'ambiance vient de la lumière, pas d'un aplat. */}
      <div
        aria-hidden
        className="animate-ember-pulse absolute inset-x-0 bottom-0 -z-10 h-2/3 bg-[radial-gradient(60%_100%_at_50%_100%,var(--ember-glow),transparent_70%)]"
      />

      {/* Plafonné : sur un grand écran vertical, `92dvh` donnait un hero de
          deux mille pixels dont l'essentiel était du vide. On veut occuper
          l'écran d'un portable, pas une affiche. */}
      {/* Centré, comme l'affiche : le titre est un objet posé au milieu de la
          scène, pas un texte aligné à gauche. */}
      <Container className="flex min-h-[min(92dvh,46rem)] flex-col items-center justify-center py-16 text-center">
        <div className="animate-fade-up flex flex-col items-center">
          {/* Le hero n'existe que sur l'accueil : la bulle aussi. */}
          <VisitorBubble event={event} />

          {/* Repli sur le fichier livré : tant qu'aucun média n'est associé à
              l'édition, le logo du restaurant s'affiche quand même. */}
          <img
            ref={logoRef}
            src={event.logo_url ?? '/leyamo.png'}
            alt={event.name}
            className="mb-7 h-24 w-24 rounded-full object-cover shadow-ember ring-1 ring-champagne-400/30"
          />

          <p className="overline text-champagne-400">
            {event.subtitle || `${t('sections.tickets')} ${event.edition_year}`}
          </p>

          {/* Composé comme sur l'affiche : capitales dorées, puis anglaise
              rouge. Voir `EventTitle`. */}
          <EventTitle name={event.name} segments={event.title_style} className="mt-4" />

          {event.slogan ? (
            <p className="mt-5 max-w-2xl text-body-lg text-content-secondary">{event.slogan}</p>
          ) : null}

          {tagline.length > 0 ? (
            <p className="overline mt-7 text-content-muted">
              {tagline.join(' · ')}
            </p>
          ) : null}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-content-secondary">
            <time dateTime={event.starts_at}>
              {formatDate(event.starts_at, i18n.language, event.timezone)}
            </time>
            {place ? <span>{place}</span> : null}
          </div>

          {countdownEnabled ? (
            <div className="mt-10">
              <Countdown target={target} textBefore={textBefore} textAfter={textAfter} />
            </div>
          ) : null}

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link ref={ticketRef} to="/billets" className={buttonClasses({ size: 'lg' })}>
              {t('cta.ticket')}
            </Link>
            <Link to="/programme" className={buttonClasses({ variant: 'outline', size: 'lg' })}>
              {t('cta.programme')}
            </Link>
            <ButtonLink
              variant="ghost"
              size="lg"
              href={googleCalendarUrl({
                title: event.name,
                details: event.slogan,
                location: [event.venue_name, event.address_line1, event.city]
                  .filter(Boolean)
                  .join(', '),
                startsAt: event.starts_at,
                endsAt: event.ends_at,
              })}
              target="_blank"
              rel="noreferrer noopener"
            >
              {t('event.addToCalendar')}
            </ButtonLink>
          </div>

          {maskPolicy ? (
            <div className="glass mt-14 max-w-2xl rounded-lg px-6 py-5">
              <p className="overline text-champagne-300">🎭 {maskPolicy.title}</p>
              <p className="mt-2 text-content-secondary">{maskPolicy.body}</p>
            </div>
          ) : null}
        </div>
      </Container>
    </header>
  )
}
